import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { CircleAlert, Info } from 'lucide-react';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      type="button"
      {...props}
    />
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={`z-50 w-fit text-balance rounded-md bg-popover px-xxs py-xs text-xs text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-xxs data-[side=left]:slide-in-from-right-xxs data-[side=right]:slide-in-from-left-xxs data-[side=top]:slide-in-from-bottom-xxs ${className}`}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

type TooltipVariantProps = {
  content: string;
} & Omit<React.ComponentProps<typeof TooltipPrimitive.Root>, 'children'>;

function WarningTooltip({ content, ...props }: TooltipVariantProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger>
        <CircleAlert className="size-m text-warning-foreground" />
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        <span className="block w-auto whitespace-pre-wrap text-wrap font-body text-body-small text-regular">
          {content}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

function InfoTooltip({ content, ...props }: TooltipVariantProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger>
        <Info className="size-m text-primary-special" />
      </TooltipTrigger>
      <TooltipContent className="max-w-60">
        <span className="block w-auto whitespace-pre-wrap text-wrap font-body text-body-small text-regular">
          {content}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  WarningTooltip,
  InfoTooltip,
};
